export const SHOP_MODULE_KEYS = [
  'sell', 'returns', 'offers', 'generators',
  'availability', 'product_content', 'quick_order', 'reviews_pro',
  'catalog_pro', 'product_family', 'refurbished', 'tier_pricing',
  'vouchers_pro', 'promotions', 'auto_discounts', 'loyalty',
] as const

export type ShopModuleKey = (typeof SHOP_MODULE_KEYS)[number]

/** `label` and `defaultOffered` mirror the backend `shopAddons.ts` (checked by `shopAddons.test.ts`). */
export interface ShopModuleDefinition {
  label: string
  defaultOffered: boolean
  /** Admin routes that redirect to /admin/abonament while the module is off. */
  adminPaths: string[]
  /** Example prices shown as placeholders on the platform billing screens. */
  pricePlaceholder: { monthly: string, yearly: string }
}

export const SHOP_MODULES: Record<ShopModuleKey, ShopModuleDefinition> = {
  sell: {
    label: 'Vinde',
    defaultOffered: true,
    adminPaths: ['/admin/vinde'],
    pricePlaceholder: { monthly: '49', yearly: '490' },
  },
  returns: {
    label: 'Retur',
    defaultOffered: true,
    adminPaths: ['/admin/returnari'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  offers: {
    label: 'Oferte',
    defaultOffered: true,
    adminPaths: ['/admin/oferte'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  generators: {
    label: 'Calculator de produse',
    defaultOffered: false,
    adminPaths: ['/admin/generatoare'],
    pricePlaceholder: { monthly: '39', yearly: '390' },
  },
  availability: {
    label: 'Statusuri & livrare',
    defaultOffered: false,
    adminPaths: ['/admin/disponibilitate'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  product_content: {
    label: 'Conținut produs',
    defaultOffered: false,
    adminPaths: ['/admin/continut-produs'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  quick_order: {
    label: 'Comandă rapidă',
    defaultOffered: false,
    adminPaths: ['/admin/comenzi-rapide'],
    pricePlaceholder: { monthly: '19', yearly: '190' },
  },
  reviews_pro: {
    label: 'Recenzii & întrebări',
    defaultOffered: false,
    adminPaths: ['/admin/intrebari'],
    pricePlaceholder: { monthly: '19', yearly: '190' },
  },
  catalog_pro: {
    label: 'Catalog Pro',
    defaultOffered: false,
    adminPaths: ['/admin/catalog-pro'],
    pricePlaceholder: { monthly: '49', yearly: '490' },
  },
  product_family: {
    label: 'Familii de produse',
    defaultOffered: false,
    adminPaths: ['/admin/familii'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  refurbished: {
    label: 'Resigilate',
    defaultOffered: false,
    adminPaths: ['/admin/resigilate'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  tier_pricing: {
    label: 'Prețuri cantitative',
    defaultOffered: false,
    adminPaths: ['/admin/preturi-cantitative'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  vouchers_pro: {
    label: 'Vouchere avansate',
    defaultOffered: false,
    adminPaths: ['/admin/vouchere'],
    pricePlaceholder: { monthly: '19', yearly: '190' },
  },
  promotions: {
    label: 'Promoții programate',
    defaultOffered: false,
    adminPaths: ['/admin/promotii'],
    pricePlaceholder: { monthly: '39', yearly: '390' },
  },
  auto_discounts: {
    label: 'Reduceri automate',
    defaultOffered: false,
    adminPaths: ['/admin/reduceri-automate'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
  loyalty: {
    label: 'Puncte de fidelitate',
    defaultOffered: false,
    adminPaths: ['/admin/fidelitate'],
    pricePlaceholder: { monthly: '29', yearly: '290' },
  },
}

function hasPathPrefix(path: string, prefix: string): boolean {
  let at = path.indexOf(prefix)
  while (at !== -1) {
    const next = path.charAt(at + prefix.length)
    if (next === '' || next === '/') return true
    at = path.indexOf(prefix, at + 1)
  }
  return false
}

/** Works with and without the `/{shop}` prefix. */
export function shopModuleForAdminPath(path: string): ShopModuleKey | null {
  for (const key of SHOP_MODULE_KEYS) {
    if (SHOP_MODULES[key].adminPaths.some((prefix) => hasPathPrefix(path, prefix))) return key
  }
  return null
}
